import { Method, KeyValuePair, Builder, HttpRequestOptions } from '../HttpRequest';
import { HttpResponseType } from '../HttpResponse';
import { HttpRequest } from '../HttpRequest';
import { HttpHeaders } from '../HttpHeaders';

/**
 * An outgoing HTTP request with an optional typed body.
 *
 * `HttpRequest` [{@link HttpRequest}] represents an outgoing request, including URL, method,
 * headers, body, and other request configuration options.
 * To modify a `HttpRequest` [{@link HttpRequest}], `Builder` [{@link Builder}] should be used.
 */
export class RequestAdapter<RequestBody> implements HttpRequest<RequestBody> {
  private method_: Method;
  private headers_: HttpHeaders;
  private body_?: RequestBody;
  private url_: string;
  private options_?: HttpRequestOptions;
  private responseType_: HttpResponseType;
  private timeout_: number;

  constructor(builder: RequestBuilder<RequestBody>) {
    this.method_ = builder.method;
    this.headers_ = builder.httpHeaders || {};
    this.body_ = builder.payload;
    this.url_ = builder.httpurl;
    this.responseType_ = builder.responseType;
    this.options_ = builder.requestOptions;
    this.timeout_ = builder.networktimeout;
  }
  /**
   * @property method [{@link Method}]
   * @returns Returns the http request method.
   */
  public get method(): Method {
    return this.method_;
  }

  /**
   * @property headers [{@link HttpHeaders}]
   * @returns Returns the http request headers.
   */
  public get headers(): HttpHeaders {
    return this.headers_;
  }

  /**
   * @property body RequestBody | undefined
   * @returns Returns the http request payload.
   */
  public get body(): RequestBody | undefined {
    return this.body_;
  }

  /**
   * @property options HttpRequestOptions
   * @returns Returns the options
   */
  public get options(): HttpRequestOptions | undefined {
    return this.options_;
  }

  /**
   * @property url String
   * @returns Returns the http encoded request url.
   */
  public get url(): string {
    return this.url_;
  }

  /**
   * @property responseType [{@link HttpResponseType}]
   * @returns Returns the http response type.
   */
  public get responseType(): HttpResponseType {
    return this.responseType_;
  }

  /**
   * @property timeout number
   * @returns Returns the http network request timeout.
   */
  public get timeout(): number {
    return this.timeout_;
  }
}

/**
 *
 * `Builder`[{@link Builder}] helps to build the [{@link HttpRequest}] request with
 * provided request body [{@link RequestBody}]
 *
 * @publicApi
 */
export class RequestBuilder<RequestBody> implements Builder<RequestBody> {
  private method_!: Method;
  private httpHeaders_!: HttpHeaders;
  private httpurl_!: string;
  private networktimeout_!: number;
  private query_!: string;
  private payload_?: RequestBody;
  private responseType_!: HttpResponseType;
  private options_?: HttpRequestOptions;

  public get method(): Method {
    return this.method_ || Method.GET;
  }

  /**
   * @property headers HttpHeaders [{@link HttpHeaders}]
   * @returns Returns the http request headers.
   */
  public get httpHeaders(): HttpHeaders {
    return this.httpHeaders_;
  }

  /**
   * @property httpurl string
   * @returns Returns the http encoded request url.
   */
  public get httpurl(): string {
    return this.options_?.disableEncode ? this.httpurl_ : encodeURI(this.httpurl_);
  }

  /**
   * @property networktimeout number
   * @returns Returns the http network request timeout.
   */
  public get networktimeout(): number {
    return this.networktimeout_;
  }

  /**
   * @property query string
   * @returns Returns the http query parameters.
   */
  public get query(): string {
    return this.query_;
  }

  /**
   * @property responseType HttpResponseType [{@link HttpResponseType}]
   * @returns Returns the http response type.
   */
  public get responseType(): HttpResponseType {
    return this.responseType_ || 'json';
  }

  public get requestOptions(): HttpRequestOptions | undefined {
    return this.options_;
  }

  /**
   * @property payload RequestBody | undefined
   * @returns Returns the http request payload.
   */
  public get payload(): RequestBody | undefined {
    return this.payload_;
  }

  /**
   * @method Method options
   * @param options Options to pass to HTTP Request
   * @returns
   */
  options(options: HttpRequestOptions): Builder<RequestBody> {
    this.options_ = options;
    return this;
  }

  /**
   * @method Method timeout
   * @params timeout Number Accepts the timeout in milliseconds.
   * @returns Returns the request with the request timeout accepted.
   */
  timeout(timeout: number): RequestBuilder<RequestBody> {
    this.networktimeout_ = timeout;
    return this;
  }

  /**
   * @method Method responseFormat
   * @params type  Accepts the format of the response.[{@link HttpResponseType}]
   * @returns Returns the request with the http response type provided.
   */
  responseFormat(type: HttpResponseType): RequestBuilder<RequestBody> {
    this.responseType_ = type;
    return this;
  }

  /**
   * @method Method url
   * @params url string Accepts the request url.
   * @returns Returns the request with the http string provided.
   */
  url(url: string): RequestBuilder<RequestBody> {
    url = url.trim();
    if (url.endsWith('/')) {
      url = url.slice(0, url.length - 1);
    }
    this.httpurl_ = url;
    return this;
  }

  /**
   * @method Method path
   * @params path string Accepts the additional path of the request url.
   * @returns Returns the request with the http path provided.
   */
  path(path: string): RequestBuilder<RequestBody> {
    this.httpurl_ += path && !path.startsWith('/') ? `/${path}` : path;
    return this;
  }

  /**
   * @method Method headers
   * @params headers Accepts the headers of the request[{@link HttpHeaders}]
   * @returns Returns the request with the headers provided.
   */
  headers(headers: HttpHeaders): RequestBuilder<RequestBody> {
    this.httpHeaders_ = headers;
    return this;
  }

  /**
   * @method Method queryParameters
   * @params Accepts the query arameters [{@link KeyValuePair}]
   * @returns Returns the request with the query parameters provided.
   */
  queryParameters(params: KeyValuePair): RequestBuilder<RequestBody> {
    let queryString: string = Object.keys(params)
      .map((key: string) => {
        return [key] + '=' + params[key];
      })
      .join('&');
    if (queryString) {
      queryString = '?' + queryString;
    }

    this.query_ = queryString;
    this.httpurl_ += this.query;

    return this;
  }

  /**
   * @method Method get [{@link Method}]
   * @returns Returns the request with the method name as 'GET'.
   */
  get(): RequestBuilder<RequestBody> {
    this.method_ = Method.GET;
    return this;
  }

  /**
   * @method Method post [{@link Method}]
   * @params payload Accepts the request body {@link RequestBody}
   * @returns Returns the 'POST' request with payload provided.
   */
  post(payload: RequestBody): RequestBuilder<RequestBody> {
    this.method_ = Method.POST;
    this.payload_ = payload;
    return this;
  }

  /**
   * @method Method put [{@link Method}]
   * @params payload Accepts the request body {@link RequestBody}
   * @returns Returns the 'PUT' request with payload provided.
   */
  put(payload?: RequestBody): RequestBuilder<RequestBody> {
    this.method_ = Method.PUT;
    this.payload_ = payload;
    return this;
  }

  /**
   * @method Method delete [{@link Method}]
   *
   * @params payload Accepts the request body {@link RequestBody}
   *
   * @returns Returns the 'DELETE' request with payload provided.
   */
  delete(payload?: RequestBody): RequestBuilder<RequestBody> {
    this.method_ = Method.DELETE;
    this.payload_ = payload;
    return this;
  }

  build(): RequestAdapter<RequestBody> {
    return new RequestAdapter<RequestBody>(this);
  }
}
